import { defineComponent as _defineComponent } from 'vue'
import {
    Search,
    HotSearch,
    HotelsSearch,
    RailwaySearch,
    HotelPage,
    SeoSearch,
    DepartureCity,
    Selection,
    NightsRange,
    DateRange,
    TouristsSelection,
    type Country,
    type Resort,
    type TouristRoom,
    DestinationPrices,
    type SearchFormCriteria,
    type SearchFormGoal,
    type SwitchCity,
 type SearchFormExpose } from '@travelata-libs/travelata-frontend';
 import { format } from 'date-fns';
import {
    ref, computed,
    unref,
} from 'vue';
import { useState } from 'vuex-composition-helpers';
import { type IMapForSearchFormState, } from '@ve/pages/serp/store';
import { NAMESPACE as MAP_FOR_SEARCH_FORM,  } from '@ve/pages/serp/store/SearchFormStore';

import type { HotelPageFlow } from '../config/hotelPageFlow';
import type { SerpVertical } from '../model/types';

interface LibSearchFormHostProps {
    vertical?: SerpVertical;
    flow?: HotelPageFlow;
    seoProps?: Record<string, unknown>;
    prices: DestinationPrices;
    authData: { user: { email: null | string, name: null | string }, accountUrl: string }
}
type SearchFormUpdate = Partial<Omit<IMapForSearchFormState, 'countries' | 'resorts' | 'hotels'>> & {
    countries?: Country[];
    resorts?: Resort[];
    hotels?: { id: number, name: string, country: number }[];
}

interface LibSearchFormHostEmits {
    (event: 'update:modelValue', data: SearchFormUpdate): void;
    (event: 'logout'): void;
    (event: 'login'): void;
    (event: 'search'): void;
    (event: 'track', goal: SearchFormGoal): void;
    (event: 'buy'): void;
    (event: 'change:city', city: SwitchCity): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LibSearchFormHost',
  props: {
    vertical: null,
    flow: null,
    seoProps: null,
    prices: null,
    authData: null
  },
  emits: ["update:modelValue", "logout", "login", "search", "track", "buy", "change:city"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'update:modelValue', data: SearchFormUpdate): void;
    (event: 'logout'): void;
    (event: 'login'): void;
    (event: 'search'): void;
    (event: 'track', goal: SearchFormGoal): void;
    (event: 'buy'): void;
    (event: 'change:city', city: SwitchCity): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    vertical?: SerpVertical;
    flow?: HotelPageFlow;
    seoProps?: Record<string, unknown>;
    prices: DestinationPrices;
    authData: { user: { email: null | string, name: null | string }, accountUrl: string }
}



const serpRef = ref<SearchFormExpose | null>(null);

const {
    checkInDateRange, 
    countries,
    departureCity,
    nightRange,
    resorts,
    touristGroup,
    hotels,
} = useState<IMapForSearchFormState>(MAP_FOR_SEARCH_FORM, ['checkInDateRange', 'countries', 'departureCity', 'nightRange', 'resorts', 'touristGroup', 'hotels'])

const SERP_PAGES = {
    tours: Search,
    hot: HotSearch,
    hotels: HotelsSearch,
    railway: RailwaySearch,
    hotelPage: HotelPage,
    seo: SeoSearch,
};

const serpPage = computed(() => SERP_PAGES[props.vertical || 'tours']);

const getNightRange = (data: IMapForSearchFormState['nightRange']): SearchFormCriteria['nights'] => {
    if (!data) return null;
    return {
        from: data.from || 0,
        to: data.to || 0,
    }
}

const getCheckInDateRange = (data: IMapForSearchFormState['checkInDateRange']): SearchFormCriteria['dateRange'] => {
    if (!data?.from || !data?.to) return null;
    return {
        from: format(data.from, 'yyyy-MM-dd'),
        to: format(data.to, 'yyyy-MM-dd'),
        daysFlexible: data.daysFlexible || 0,
    }
}
const getDepartureCity = (data: IMapForSearchFormState['departureCity']): SearchFormCriteria['fromCity'] => data || null;

const getTouristsGroup = (data: IMapForSearchFormState['touristGroup']): SearchFormCriteria['tourists'] => {
    if (!data) return null;
    const all = [data, ...data.touristsByRooms || []];
    return { rooms: all.map((t) => ({ adults: t?.adults || 0, childrenAges: (t?.ages || []) }))};
}

const adaptResorts = (resorts: IMapForSearchFormState['resorts'], country: Country): Resort[] => {
    return resorts?.map((r) => ({
        id: r.id,
        name: r.name,
        isPopular: r.isPopular,
        minPrice: r.minPrice ?? null,
        countryId: country.id,
        countryName: country.name,
        atFiltering: r.atFiltering,
    }))
};

const getDestination = (_countries: IMapForSearchFormState['countries'], _resorts: IMapForSearchFormState['resorts'], _hotels: IMapForSearchFormState['hotels']): SearchFormCriteria['destination'] => {
    const country: Country & { resorts: IMapForSearchFormState['resorts'] } = _countries?.[0];
    if(!country) return null;
    const libCountry: Country = { ...country, resorts: adaptResorts(country.resorts, country) };
    if (_hotels?.length > 0) {
        return {
            kind: 'hotels',
            hotels: _hotels.map((h) => ({ id: h.id, name: h.name, countryId: country.id, countryName: country.name })),
            country: libCountry,
        }
    }
    const libResorts: Resort[] = adaptResorts(_resorts, country);
    if(libResorts.length > 0) {
        return {
            kind: 'resorts',
            country: libCountry,
            resorts: libResorts,
        }
    };
    return {
        kind: 'country',
        country: libCountry,
    }
}

function getUpdateFromCity(city: DepartureCity | null): SearchFormUpdate | null {
    if (!city) return null;           
    return {
        departureCity: {
            id: city.id,
            name: city.name,
            countriesWithDirectFlights: city.countriesWithDirectFlights ?? [],
        }
    }
}
function getUpdateDestination(selection: Selection | null): SearchFormUpdate | null {
    if (!selection) return null;
    return {
        resorts: selection.kind === 'resorts' ? selection.resorts : [],
        countries: selection.kind !== 'none' ? [selection.country] : [],
        hotels: selection.kind === 'hotels' ? selection.hotels.map((h) => ({ id: h.id, name: h.name, country: h.countryId })) : [],
    }
};

function getUpdateNights(range: NightsRange | null): SearchFormUpdate | null {
    return {
        nightRange: {
            from: range?.from || 0,
            to: range?.to || 0,
        }
    }
}
function getUpdateCheckInDate(value: DateRange | null): SearchFormUpdate | null {
    if (!value?.from || !value?.to) return null;
    return {
        checkInDateRange: {
            from: new Date(value.from),
            to: new Date(value.to),
            daysFlexible: value.daysFlexible,
        }
    }
}
const adaptTouristsGroup = (t: TouristRoom) => ({
    adults: t.adults,
    kids: t.childrenAges.filter((age) => age > 1).length,
    infants:  t.childrenAges.filter((age) => age <= 1).length,
    ages: t.childrenAges,
    touristsByRooms: [],
})
function getUpdatedTourists(tourists: TouristsSelection | null): SearchFormUpdate | null {
    if (!tourists) return null;
    const [first, ...other] = tourists.rooms;
    if(!first) return null;

    return {
        touristGroup: {
            ...adaptTouristsGroup(first),
            touristsByRooms: other.map(adaptTouristsGroup),
        }
    }
}


const model = computed<SearchFormCriteria>({
    get: () => {
        return {
            fromCity: getDepartureCity(departureCity.value),
            destination: getDestination(countries.value, resorts.value, hotels.value),
            nights: getNightRange(nightRange.value),
            dateRange: getCheckInDateRange(checkInDateRange.value),
            tourists: getTouristsGroup(touristGroup.value),
        }
    },
    set: (data: SearchFormCriteria) => {
        emit('update:modelValue', {
            ...getUpdateFromCity(data.fromCity),
            ...getUpdateCheckInDate(data.dateRange),
            ...getUpdateDestination(data.destination),
            ...getUpdateNights(data.nights),
            ...getUpdatedTourists(data.tourists),
        })
    },
})

expose({
    validate: (): boolean => (serpRef.value ? serpRef.value.validate() : true),
});

return { __sfc: true,props, emit, serpRef, checkInDateRange, countries, departureCity, nightRange, resorts, touristGroup, hotels, SERP_PAGES, serpPage, getNightRange, getCheckInDateRange, getDepartureCity, getTouristsGroup, adaptResorts, getDestination, getUpdateFromCity, getUpdateDestination, getUpdateNights, getUpdateCheckInDate, adaptTouristsGroup, getUpdatedTourists, model }
}

})